import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DAY_SHORT_KEYS, DAY_FULL_KEYS, DAY_LETTER_KEYS } from '../data';
import { getMondayFirstDayIndex } from '../dayIndex';
import { useBuilderContext } from '../context/BuilderContext';

export function StepInfo() {
  const { t } = useTranslation();
  const { form, setForm } = useBuilderContext();
  const today = new Date().toISOString().split('T')[0];
  const isWorkdays = JSON.stringify(form.activeDays) === JSON.stringify([0, 1, 2, 3, 4]);
  const isCustom = form.activeDays.length > 0 && !isWorkdays;
  const isSingleDay = !!form.startDate && form.startDate === form.expirationDate;
  const singleDayIndex = isSingleDay ? getMondayFirstDayIndex(form.startDate) : null;

  useEffect(() => {
    if (singleDayIndex === null) return;
    setForm(p => {
      if (p.activeDays.length === 1 && p.activeDays[0] === singleDayIndex) return p;
      return { ...p, activeDays: [singleDayIndex] };
    });
  }, [singleDayIndex, setForm]);

  function toggleDay(i: number) {
    setForm(p => ({
      ...p,
      activeDays: p.activeDays.includes(i)
        ? p.activeDays.filter(d => d !== i)
        : [...p.activeDays, i].sort(),
    }));
  }

  return (
    <div className="builder-section">
      <h3><i className="fas fa-info-circle" /> {t('admin.builderInfoTitle')}</h3>

      <div className="form-group">
        <label>{t('admin.builderInternalId')} *</label>
        <input
          type="text"
          className="form-input"
          value={form.name}
          placeholder={t('admin.builderInternalIdPlaceholder')}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
        />
        <small className="form-hint bld-internal-hint">
          <i className="fas fa-lock" /> {t('admin.builderInternalIdHint')}
        </small>
      </div>

      <div className="form-group">
        <label>{t('admin.builderDescription')}</label>
        <input
          type="text"
          className="form-input"
          value={form.description}
          placeholder={t('admin.builderDescriptionPlaceholder')}
          onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
        />
      </div>

      <div className="form-group">
        <label>{t('admin.builderSurveyTypeStar')} *</label>
        <div className="survey-type-cards">
          <div
            className={`survey-type-card${form.surveyType === 'general' ? ' selected' : ''}`}
            onClick={() => setForm(p => ({ ...p, surveyType: 'general' }))}
          >
            <i className="fas fa-calendar-alt" />
            <strong>{t('admin.builderTypeGeneral')}</strong>
            <span>{t('admin.builderTypeGeneralDesc')}</span>
          </div>
          <div
            className={`survey-type-card${form.surveyType === 'personalizada' ? ' selected' : ''}`}
            onClick={() => setForm(p => ({ ...p, surveyType: 'personalizada' }))}
          >
            <i className="fas fa-video" />
            <strong>{t('admin.builderTypePersonalized')}</strong>
            <span>{t('admin.builderTypePersonalizedDesc')}</span>
          </div>
        </div>
      </div>

      <div className="form-group">
        <label>{t('admin.builderPeriod')} *</label>
        <div className="date-range-row">
          <div className="date-range-field">
            <span className="date-range-label">
              <i className="fas fa-calendar-plus" /> {t('admin.builderStartLabel')}
            </span>
            <input
              type="date"
              className="form-input"
              value={form.startDate}
              min={today}
              onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))}
            />
          </div>
          <div className="date-range-sep"><i className="fas fa-arrow-right" /></div>
          <div className="date-range-field">
            <span className="date-range-label">
              <i className="fas fa-calendar-times" /> {t('admin.builderEndLabel')}
            </span>
            <input
              type="date"
              className="form-input"
              value={form.expirationDate}
              min={form.startDate || today}
              onChange={e => setForm(p => ({ ...p, expirationDate: e.target.value }))}
            />
          </div>
        </div>
        <small className="form-hint">{t('admin.builderPeriodHint')}</small>
      </div>

      <div className="form-group">
        <label>{t('admin.builderActiveDaysLabel')}</label>
        <div className="day-presets">
          <button
            type="button"
            className={`day-preset-btn${isWorkdays ? ' active' : ''}`}
            disabled={isSingleDay}
            onClick={() => setForm(p => ({ ...p, activeDays: [0, 1, 2, 3, 4] }))}
          >
            <i className="fas fa-briefcase" /> {t('admin.workdays')}
          </button>
          {isCustom && (
            <span className="day-custom-tag">
              <i className="fas fa-sliders-h" /> {t('admin.builderCustomTag')}
            </span>
          )}
        </div>
        <div className="day-pills">
          {DAY_SHORT_KEYS.map((key, i) => (
            <button
              key={i}
              type="button"
              className={`day-pill${form.activeDays.includes(i) ? ' active' : ''}`}
              disabled={isSingleDay}
              onClick={() => toggleDay(i)}
            >
              <span className="day-pill-name">{t(key)}</span>
              <span className="day-pill-num">{t(DAY_LETTER_KEYS[i])}</span>
            </button>
          ))}
        </div>
        <small className="form-hint">
          {form.activeDays.length === 0
            ? t('admin.activeDaysNone')
            : form.activeDays.length === 7
            ? t('admin.activeDaysAll')
            : `${t('admin.activeDaysPrefix')} ${form.activeDays.map(i => t(DAY_FULL_KEYS[i])).join(', ')}`}
        </small>
        {isSingleDay && (
          <small className="form-hint">
            <i className="fas fa-lock" /> {t('admin.builderActiveDaysLockedHint')}
          </small>
        )}
      </div>
    </div>
  );
}
